/**
 * Quyền giao diện của tài khoản cổng tenant, tính từ `GET /users/me/context`
 * (ADR-0034) thay cho việc đoán từ role trong token.
 *
 * Hàm thuần: không gọi HTTP, không chạm Zustand, không đọc registry. Bản đồ
 * đường dẫn → dịch vụ → phân hệ đi vào bằng tham số (`BanDoDichVu`), dựng ở
 * `lib/dich-vu-cong.ts` từ `nav-config.ts` và `route-permissions.ts`.
 *
 * ⚠️ Đây là **điều hướng phía giao diện**. Chặn thật nằm ở Gateway (`X-Permissions`
 * là tập mã module, ADR-0003) và ở từng service.
 */
import type { BanDoDichVu, DichVuVung, MyContext, QuyenCongTenant } from './types.ts';

/**
 * Role lớp 1 có toàn quyền trong mọi module `ACTIVE` thuộc gói của tenant
 * (ADR-0077 mục 2): backend đã cộng mọi mã quyền module vào `permissions`, giao
 * diện mở mọi màn và mọi thao tác của dịch vụ trong `dich_vu`.
 */
export const VAI_TRO_QUAN_TRI_TENANT = 'TENANT_ADMIN';

/**
 * Hành động giao diện cấp cho quản trị tổ chức trên mỗi phân hệ được dùng.
 * Không có `HUY`, `ISSUE`, `APPROVE`: đó là thao tác của cổng nền tảng
 * (thuê bao, license), không thuộc cổng tenant.
 */
export const HANH_DONG_QUAN_TRI: readonly string[] = ['CREATE', 'DELETE', 'EXPORT', 'READ', 'UPDATE'];

/**
 * Dịch vụ chỉ người quản trị tổ chức được dùng.
 *
 * `cau-hinh-he-thong` không có `vung_api` (ADR-0019 mục 2.1) nên không suy được
 * từ `mods`; nội dung của nó — vai trò, người dùng, cơ cấu tổ chức, bảo mật,
 * khoá API — là việc quản trị tenant, không phải việc của người dùng nghiệp vụ.
 */
export const DICH_VU_CHI_QUAN_TRI: readonly string[] = ['cau-hinh-he-thong'];

/**
 * Phân hệ cấu hình của một dịch vụ nghiệp vụ: chỉ quản trị tổ chức được cấp.
 *
 * Màn cấu hình OCR và cấu hình xử lý văn bản nằm trong nhóm của dịch vụ của chúng
 * (ADR-UI-0015) để gói của tenant quyết định có hiện hay không. Nhưng chúng vẫn là
 * việc quản trị như khi còn ở `cau-hinh-he-thong`: người dùng nghiệp vụ dùng OCR
 * không vì thế mà được sửa loại chứng từ. Thiếu bảng này thì chuyển nhóm sẽ lặng lẽ
 * mở màn cấu hình cho mọi người dùng của dịch vụ.
 */
export const PHAN_HE_CHI_QUAN_TRI: readonly string[] = ['OCR_CONFIG', 'TEXT_CONFIG'];

/** Đoạn đầu của một đường dẫn gốc: `/tri-thuc/upload` → `tri-thuc`, `/` → `''`. */
export function doanDau(duongDanGoc: string): string {
  return duongDanGoc.split('/').filter(Boolean)[0] ?? '';
}

/**
 * Bảng đoạn đầu → dịch vụ, từ các nhóm điều hướng có khai `dichVu`.
 *
 * Ném lỗi khi một đoạn rơi vào hai dịch vụ: khi đó không còn biết `/xu-ly/...`
 * thuộc gói nào, và lặng lẽ chọn một bên là mở nhầm màn ngoài gói.
 */
export function lapDichVuTheoDoan(
  nhom: ReadonlyArray<{ dichVu?: string; hrefs: readonly string[] }>,
): Map<string, string> {
  const ra = new Map<string, string>();
  for (const { dichVu, hrefs } of nhom) {
    if (!dichVu) continue;
    for (const href of hrefs) {
      const doan = doanDau(href);
      const daCo = ra.get(doan);
      if (daCo && daCo !== dichVu) {
        throw new Error(`Đoạn "/${doan}" thuộc cả "${daCo}" lẫn "${dichVu}"`);
      }
      ra.set(doan, dichVu);
    }
  }
  return ra;
}

/** Dịch vụ của một đường dẫn gốc, hoặc `undefined` nếu nhánh đó không thuộc dịch vụ nào. */
export function dichVuCuaDuongDan(
  duongDanGoc: string,
  dichVuTheoDoan: ReadonlyMap<string, string>,
): string | undefined {
  return dichVuTheoDoan.get(doanDau(duongDanGoc));
}

/**
 * Dịch vụ người này được dùng.
 *
 * - Dịch vụ phải có trong `dich_vu` của tenant (gói, ghi đè, ân hạn — backend đã tính).
 * - `TENANT_ADMIN`: mọi dịch vụ đó.
 * - Role khác (`TENANT_USER`, role lớp 2 của tenant): dịch vụ có `vung_api` giao với
 *   `mods`. Dịch vụ không có `vung_api` (`tong-quan`) thì ai cũng dùng, trừ
 *   `DICH_VU_CHI_QUAN_TRI`. Dịch vụ không có trong danh mục thì không dùng — chưa
 *   biết nó cần module nào thì không mở.
 *
 * `tri-thuc` là vùng dùng chung của Tri thức AI và Trợ lý AI (ADR-0019 mục 2.1): người
 * chỉ có quyền `tri-thuc` thấy **cả hai** nhóm nếu tenant thuê cả hai.
 */
export function dichVuDuocDung(ctx: MyContext, danhMuc: readonly DichVuVung[]): string[] {
  const laQuanTri = ctx.roles.platform.includes(VAI_TRO_QUAN_TRI_TENANT);
  if (laQuanTri) return [...ctx.dich_vu];

  const mods = new Set(ctx.mods);
  const vungTheoMa = new Map(danhMuc.map((d) => [d.ma, d.vung_api]));
  return ctx.dich_vu.filter((ma) => {
    if (DICH_VU_CHI_QUAN_TRI.includes(ma)) return false;
    const vung = vungTheoMa.get(ma);
    if (vung === undefined) return false;
    if (vung.length === 0) return true;
    return vung.some((v) => mods.has(v));
  });
}

/**
 * Quyền giao diện từ `MyContext`: nhãn vai trò, dịch vụ được dùng, và `<PHÂN_HỆ>.READ`
 * cho mọi phân hệ có ít nhất một tiền tố đường dẫn nằm trong dịch vụ được dùng, trừ
 * `PHAN_HE_CHI_QUAN_TRI` với người không phải quản trị tổ chức.
 * Quản trị tổ chức nhận đủ `HANH_DONG_QUAN_TRI` trên các phân hệ đó (ADR-0077), nên
 * không mục nào bị ẩn vì thiếu quyền ghi.
 *
 * Một phân hệ có thể có mặt ở hai dịch vụ (`ROLE_CONFIG` ở cả `/he-thong/vai-tro` lẫn
 * `/bao-cao-doanh-nghiep/nguon`). Quyền phân hệ khi đó mở cả hai nhánh, nên cổng quyền
 * còn xét thêm dịch vụ của đường dẫn (`PermissionGuard`).
 *
 * Kèm theo là **nguyên văn** các mã quyền module của `ctx.permissions` (dạng
 * `<module>:<tài nguyên>:<hành động>`, ví dụ `tri-thuc:tep:share`): màn của module đọc
 * thẳng mã đó để ẩn nút (`useModulePermission`), như SDK kiểm ở backend. Hai dạng không
 * trùng nhau nhờ dấu `:`.
 *
 * TODO(G2-D1-6): chưa ánh xạ hành động ghi sang phân hệ RBAC (`<PHÂN_HỆ>.CREATE`…) cho các màn
 * cũ; màn tri thức dùng thẳng mã module (ADR-0075). Cần Dev 3 rà cách này.
 */
export function tinhQuyenCongTenant(
  ctx: MyContext,
  danhMuc: readonly DichVuVung[],
  banDo: BanDoDichVu,
): QuyenCongTenant {
  const dichVu = dichVuDuocDung(ctx, danhMuc);
  const duocDung = new Set(dichVu);

  const laQuanTri = ctx.roles.platform.includes(VAI_TRO_QUAN_TRI_TENANT);

  const phanHe = new Set<string>();
  for (const [tienTo, ma] of banDo.phanHeTheoTienTo) {
    if (!laQuanTri && PHAN_HE_CHI_QUAN_TRI.includes(ma)) continue;
    const dv = dichVuCuaDuongDan(tienTo, banDo.dichVuTheoDoan);
    if (dv !== undefined && duocDung.has(dv)) phanHe.add(ma);
  }

  const hanhDong = laQuanTri ? HANH_DONG_QUAN_TRI : ['READ'];
  return {
    khoaVaiTro: laQuanTri ? 'vaiTro.quanTriToChuc' : 'vaiTro.nguoiDungToChuc',
    dichVu,
    permissions: [
      ...[...phanHe].sort().flatMap((ma) => hanhDong.map((hd) => `${ma}.${hd}`)),
      ...ctx.permissions.filter((p) => p.includes(':')).sort(),
    ],
  };
}
